import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import DonutChart from '../components/charts/DonutChart';
import MultiLineChart from '../components/charts/MultiLineChart';
import { FiGrid, FiCalendar, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const AccountsDashboard = () => {
  const { isAuthenticated, company } = useAuth();
  const [bizStats, setBizStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [recurringActiveCount, setRecurringActiveCount] = useState(0);
  const [recurringRows, setRecurringRows] = useState([]);
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [sourceBreakdown, setSourceBreakdown] = useState({ total: 0, sources: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(() => {
    try {
      const raw = window.localStorage.getItem('accounts.viewMode');
      return raw === 'calendar' ? 'calendar' : 'stats';
    } catch (_) { return 'stats'; }
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedCalendarDay, setSelectedCalendarDay] = useState(null); // { dateStr, day }
  const [calendarFilters, setCalendarFilters] = useState({ source: '', status: '' });
  const [dayTransactions, setDayTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [pendingByDay, setPendingByDay] = useState({}); // { 'YYYY-MM-DD': amount }
  const [paidByDay, setPaidByDay] = useState({}); // { 'YYYY-MM-DD': amount }
  const [calendarInvoices, setCalendarInvoices] = useState([]); // invoices for month, fetched by effective date
  const [calendarInvoicesLoading, setCalendarInvoicesLoading] = useState(false);
  const [paymentDetailsModal, setPaymentDetailsModal] = useState({ open: false, loading: false, error: '', invoice: null });
  const [pdfModal, setPdfModal] = useState({ open: false, url: '', label: '', loading: false });
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const now = new Date();
    const fyStartMonth = 3; // April (0-indexed)
    const fyStartYear = now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
    // Use YYYY-MM-DD format directly to avoid timezone issues
    const startDate = `${fyStartYear}-04-01`;
    const endDate = `${fyStartYear + 1}-03-31`;
    return { startDate, endDate, label: 'Current FY' };
  });

  const formatInr = (n) => {
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
    } catch {
      return `₹${Number(n || 0).toFixed(2)}`;
    }
  };

  const openInvoicePdf = async (invoiceId, label) => {
    if (!invoiceId) return;
    setPdfModal({ open: true, url: '', label: label || 'Invoice', loading: true });
    try {
      const { data } = await api.get(`/api/invoices/${invoiceId}/pdf-url`);
      const url = data?.url;
      if (url) setPdfModal(m => ({ ...m, url, loading: false }));
      else setPdfModal({ open: false, url: '', label: '', loading: false });
    } catch (e) {
      console.error('Failed to open invoice PDF', e);
      setPdfModal({ open: false, url: '', label: '', loading: false });
    }
  };

  const openQuotePdf = async (quoteId, label) => {
    if (!quoteId) return;
    setPdfModal({ open: true, url: '', label: label || 'Quote', loading: true });
    try {
      const { data } = await api.get(`/api/quotes/${quoteId}/pdf-url`);
      const url = data?.url;
      if (url) setPdfModal(m => ({ ...m, url, loading: false }));
      else setPdfModal({ open: false, url: '', label: '', loading: false });
    } catch (e) {
      console.error('Failed to open quote PDF', e);
      setPdfModal({ open: false, url: '', label: '', loading: false });
    }
  };

  const openProformaPdf = async (proformaId, label) => {
    if (!proformaId) return;
    setPdfModal({ open: true, url: '', label: label || 'Proforma', loading: true });
    try {
      const { data } = await api.get(`/api/proformas/${proformaId}/pdf-url`);
      const url = data?.url;
      if (url) setPdfModal(m => ({ ...m, url, loading: false }));
      else setPdfModal({ open: false, url: '', label: '', loading: false });
    } catch (e) {
      console.error('Failed to open proforma PDF', e);
      setPdfModal({ open: false, url: '', label: '', loading: false });
    }
  };

  const closePdfModal = () => setPdfModal({ open: false, url: '', label: '', loading: false });

  const openPaymentDetails = async (invoiceId) => {
    if (!invoiceId) return;
    setPaymentDetailsModal({ open: true, loading: true, error: '', invoice: null });
    try {
      const { data } = await api.get(`/api/invoices/${invoiceId}`);
      setPaymentDetailsModal({ open: true, loading: false, error: '', invoice: data || null });
    } catch (e) {
      setPaymentDetailsModal({ open: true, loading: false, error: 'Failed to load payment details', invoice: null });
    }
  };

  const closePaymentDetails = () => setPaymentDetailsModal({ open: false, loading: false, error: '', invoice: null });

  // Helper to extract YYYY-MM-DD from date string (handles both ISO and YYYY-MM-DD)
  const toDateStr = (val) => {
    if (!val) return undefined;
    // If already YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    // Parse as date and extract LOCAL date components to avoid timezone shifts
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return undefined;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return val.slice(0, 10);
    }
  };

  // Local "today" as YYYY-MM-DD (avoid timezone drift)
  const todayYmd = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Calendar "effective date" rule:
  // - If PAID: use paymentDate
  // - Else: use dueDate
  // - Else: fall back to invoice date
  const invoiceEffectiveDateStr = (inv) => {
    if (!inv) return undefined;
    const status = String(inv.status || '').toUpperCase();
    if (status === 'PAID') {
      return toDateStr(inv.paymentDate) || toDateStr(inv.dueDate) || toDateStr(inv.date);
    }
    return toDateStr(inv.dueDate) || toDateStr(inv.date);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const fromParam = toDateStr(selectedDateRange?.startDate);
        const toParam = toDateStr(selectedDateRange?.endDate);
        const [statsRes, sourceRes, trendsRes] = await Promise.all([
          api.get('/api/company/business-stats', { params: { fromDate: fromParam, toDate: toParam } }),
          api.get('/api/company/revenue-by-source', { params: { fromDate: fromParam, toDate: toParam } }),
          api.get('/api/company/business-trends', { params: { fromDate: fromParam, toDate: toParam } })
        ]);
        setBizStats(statsRes.data.data);
        setSourceBreakdown(sourceRes.data.data || { total: 0, sources: [] });
        setTrends(trendsRes.data.data || null);
      } catch (e) {
        setError('Failed to load accounts overview');
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated && company?.id) fetchData();
  }, [isAuthenticated, company?.id, selectedDateRange?.startDate, selectedDateRange?.endDate]);

  // Recurring billing: fetch active schedules count
  useEffect(() => {
    const fetchRecurring = async () => {
      try {
        const res = await api.get('/api/recurring-invoices', { params: { status: 'ACTIVE', pageSize: 1 } });
        setRecurringActiveCount(Number(res.data?.total || 0));
      } catch (_) {
        setRecurringActiveCount(0);
      }
    };
    if (isAuthenticated && company?.id) fetchRecurring();
  }, [isAuthenticated, company?.id]);

  // Recurring billing: fetch upcoming schedules list (lightweight)
  useEffect(() => {
    const fetchRecurringRows = async () => {
      if (!isAuthenticated || !company?.id) {
        setRecurringRows([]);
        return;
      }
      setRecurringLoading(true);
      try {
        const res = await api.get('/api/recurring-invoices', {
          params: { status: 'ACTIVE', page: 1, pageSize: 80, companyId: company.id }
        });
        setRecurringRows(res.data?.data || []);
      } catch (e) {
        setRecurringRows([]);
      } finally {
        setRecurringLoading(false);
      }
    };
    fetchRecurringRows();
  }, [isAuthenticated, company?.id]);

  useEffect(() => {
    try { window.localStorage.setItem('accounts.viewMode', viewMode); } catch (_) {}
  }, [viewMode]);

  // When calendar month changes, update selectedDateRange to fetch data for that month
  useEffect(() => {
    if (viewMode === 'calendar') {
      const { year, month } = calendarMonth;
      // Use YYYY-MM-DD format to avoid timezone issues
      const startStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      setSelectedDateRange({
        startDate: startStr,
        endDate: endStr,
        label: `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month]} ${year}`
      });
    }
  }, [calendarMonth, viewMode]);

  // When switching to calendar view, reset to current month and auto-select today
  const handleViewModeChange = (mode) => {
    if (mode === 'calendar' && viewMode !== 'calendar') {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      setCalendarMonth({ year, month });
      // Auto-select today
      const day = now.getDate();
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setSelectedCalendarDay({ dateStr, day });
    }
    setViewMode(mode);
  };

  // Auto-select 1st of month or today when changing months
  useEffect(() => {
    if (viewMode === 'calendar') {
      const { year, month } = calendarMonth;
      const now = new Date();
      const isCurrentMonth = now.getFullYear() === year && now.getMonth() === month;
      const day = isCurrentMonth ? now.getDate() : 1;
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setSelectedCalendarDay({ dateStr, day });
    }
  }, [calendarMonth.year, calendarMonth.month, viewMode]);

  // Fetch invoices for calendar month using EFFECTIVE dateType so:
  // - PAID invoices land on paymentDate
  // - Unpaid invoices land on dueDate (or invoice date fallback)
  useEffect(() => {
    const fetchCalendarInvoices = async () => {
      if (viewMode !== 'calendar' || !isAuthenticated || !company?.id) {
        setCalendarInvoices([]);
        setPendingByDay({});
        setPaidByDay({});
        return;
      }
      const { year, month } = calendarMonth;
      const startStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      setCalendarInvoicesLoading(true);
      setLoadingTransactions(true);
      try {
        const collected = [];
        let page = 1;
        const pageSize = 100; // server caps to 100
        let total = Infinity;
        while (collected.length < total && page <= 20) {
          const res = await api.get('/api/invoices', {
            params: {
              fromDate: startStr,
              toDate: endStr,
              dateType: 'EFFECTIVE',
              page,
              pageSize
            }
          });
          const data = res.data?.data || [];
          const t = Number(res.data?.total || 0);
          total = Number.isFinite(t) && t > 0 ? t : (page === 1 ? data.length : collected.length);
          collected.push(...data);
          if (!data.length) break;
          if (data.length < pageSize) break;
          page += 1;
        }

        setCalendarInvoices(collected);

        // Build paid/pending maps for calendar cells
        const paidMap = {};
        const pendingMap = {};
        (collected || []).forEach(inv => {
          const status = String(inv?.status || '').toUpperCase();
          const key = invoiceEffectiveDateStr(inv);
          if (!key) return;
          if (status === 'CANCELLED') return;
          if (status === 'PAID') {
            if (!paidMap[key]) paidMap[key] = 0;
            paidMap[key] += Number(inv.total || 0);
          } else {
            // outstanding balance
            const bal = Math.max(0, Number(inv.total || 0) - Number(inv.amountPaid || 0));
            if (bal <= 0) return;
            if (!pendingMap[key]) pendingMap[key] = 0;
            pendingMap[key] += bal;
          }
        });
        setPaidByDay(paidMap);
        setPendingByDay(pendingMap);
      } catch (e) {
        console.error('Error fetching calendar invoices:', e);
        setCalendarInvoices([]);
        setPendingByDay({});
        setPaidByDay({});
      } finally {
        setCalendarInvoicesLoading(false);
        setLoadingTransactions(false);
      }
    };
    fetchCalendarInvoices();
  }, [viewMode, calendarMonth, isAuthenticated, company?.id]);

  // Derive day transactions from loaded calendar invoices + filters, using the same effective-date rule
  useEffect(() => {
    if (viewMode !== 'calendar' || !selectedCalendarDay?.dateStr) {
      setDayTransactions([]);
      return;
    }
    const dayKey = selectedCalendarDay.dateStr;
    let list = (calendarInvoices || []).filter(inv => invoiceEffectiveDateStr(inv) === dayKey);

    // Apply filters (client-side) so they match the effective-date grouping
    if (calendarFilters.source) {
      list = list.filter(inv => String(inv.createdFrom || '').toUpperCase() === String(calendarFilters.source || '').toUpperCase());
    }
    if (calendarFilters.status) {
      const s = String(calendarFilters.status || '').toUpperCase();
      if (s === 'PAID' || s === 'OVERDUE') {
        list = list.filter(inv => String(inv.status || '').toUpperCase() === s);
      } else if (s === 'PARTIAL') {
        list = list.filter(inv => (String(inv.paymentStatus || '').toUpperCase() === 'PARTIALLY_PAID') || (Number(inv.amountPaid || 0) > 0 && String(inv.status || '').toUpperCase() !== 'PAID'));
      } else if (s === 'DUE') {
        // "Due" = not paid/cancelled and not overdue (backend returns derived OVERDUE)
        list = list.filter(inv => !['PAID', 'CANCELLED', 'OVERDUE'].includes(String(inv.status || '').toUpperCase()));
      } else {
        list = list.filter(inv => String(inv.status || '').toUpperCase() === s);
      }
    }

    // Keep newest first (fallback to id)
    list.sort((a, b) => {
      const ad = invoiceEffectiveDateStr(a) || '';
      const bd = invoiceEffectiveDateStr(b) || '';
      if (bd !== ad) return String(bd).localeCompare(String(ad));
      return Number(b?.id || 0) - Number(a?.id || 0);
    });
    setDayTransactions(list);
  }, [viewMode, selectedCalendarDay?.dateStr, calendarInvoices, calendarFilters.source, calendarFilters.status]);

  // ─── Matte palette (shared vibe with Quotes/Invoices/Proformas stats) ───────
  const matte = useMemo(() => ({
    blue: '#74a0d6',
    purple: '#8a7bb8',
    green: '#79b38a',
    red: '#c57878',
    // Slightly more orange amber (less “gold”) for better contrast on dark bg
    amber: '#e39a2b',
    teal: '#55a39b',
    rose: '#b77a98',
    indigo: '#7a76b5',
    allGrey: '#9ca3af',
    rust: '#b3784f'
  }), []);

  // ─── Source labels and colors ───────────────────────────────────────────────
  const sourceLabels = useMemo(() => ({
    POS: 'POS',
    DIRECT: 'Direct',
    CONVERTED_QUOTE: 'Converted Quote',
    CUSTOMER_ORDER: 'Customer Order',
    QUOTE_ADVANCE: 'Quote Advance',
    RECURRING_PROFORMA: 'Recurring'
  }), []);

  const sourceColors = useMemo(() => ({
    POS: matte.blue,
    DIRECT: matte.teal,
    CONVERTED_QUOTE: matte.amber,
    CUSTOMER_ORDER: matte.rose,
    QUOTE_ADVANCE: matte.purple,
    RECURRING_PROFORMA: matte.rust
  }), [matte]);

  // ─── All sources breakdown ──────────────────────────────────────────────────
  const allSources = useMemo(() => {
    const list = Array.isArray(sourceBreakdown?.sources) ? sourceBreakdown.sources : [];
    return (list || [])
      .map(s => ({
        source: String(s.source || ''),
        label: sourceLabels[s.source] || String(s.source || '').replace(/_/g, ' '),
        amount: Number(s.amount || 0),
        count: Number(s.count || 0),
        color: sourceColors[s.source] || matte.indigo
      }))
      .filter(s => !!s.source)
      .sort((a, b) => (b.amount || 0) - (a.amount || 0));
  }, [sourceBreakdown, sourceLabels, sourceColors, matte.indigo]);

  // ─── Revenue trend data ─────────────────────────────────────────────────────
  const revenuePerDay = useMemo(() => trends?.revenuePerDay || [], [trends]);
  const quotesPerDay = useMemo(() => trends?.quotesPerDay || [], [trends]);
  const ordersPerDay = useMemo(() => trends?.ordersPerDay || [], [trends]);
  const revenueBySourcePerDay = useMemo(() => trends?.revenueBySourcePerDay || [], [trends]);
  const totalRevenueFromTrend = useMemo(() => revenuePerDay.reduce((s, d) => s + (d.amount || 0), 0), [revenuePerDay]);
  const peakRevenueDay = useMemo(() => {
    if (!revenuePerDay.length) return null;
    const max = Math.max(...revenuePerDay.map(d => d.amount || 0));
    return revenuePerDay.find(d => d.amount === max) || null;
  }, [revenuePerDay]);

  const revenueBySourceSeries = useMemo(() => {
    // Prefer showing series in same order as "allSources" (highest revenue first)
    const bySrc = Object.fromEntries((revenueBySourcePerDay || []).map(r => [String(r.source || '').toUpperCase(), r.data || []]));
    const ordered = (allSources || []).map(s => String(s.source || '').toUpperCase()).filter(Boolean);
    const uniq = Array.from(new Set(ordered));
    const series = uniq
      .map((src) => {
        const data = bySrc[src] || [];
        const meta = (allSources || []).find(s => String(s.source || '').toUpperCase() === src);
        return {
          name: meta?.label || src,
          color: meta?.color || matte.indigo,
          data
        };
      })
      .filter(s => Array.isArray(s.data) && s.data.some(d => Number(d?.amount || 0) > 0))
      .slice(0, 4); // keep it readable like the screenshot
    return series;
  }, [revenueBySourcePerDay, allSources, matte.indigo]);

  // ─── Upcoming recurring (client-side derived) ───────────────────────────────
  const recurringUpcoming = useMemo(() => {
    const today = todayYmd();
    const list = Array.isArray(recurringRows) ? recurringRows : [];
    return list
      .filter(r => String(r?.status || '').toUpperCase() === 'ACTIVE')
      .filter(r => {
        const next = String(r?.nextInvoiceDate || '');
        return next && /^\d{4}-\d{2}-\d{2}$/.test(next) && next >= today;
      })
      .sort((a, b) => String(a?.nextInvoiceDate || '').localeCompare(String(b?.nextInvoiceDate || '')))
      .slice(0, 6);
  }, [recurringRows]);

  const recurringDueMeta = useMemo(() => {
    const today = todayYmd();
    let dueToday = 0;
    let overdue = 0;
    for (const r of (recurringRows || [])) {
      if (String(r?.status || '').toUpperCase() !== 'ACTIVE') continue;
      const next = String(r?.nextInvoiceDate || '');
      if (!next || !/^\d{4}-\d{2}-\d{2}$/.test(next)) continue;
      if (next === today) dueToday += 1;
      else if (next < today) overdue += 1;
    }
    return { dueToday, overdue };
  }, [recurringRows]);

  const fmtYmd = (ymd) => {
    try {
      if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '-';
      // Force local midnight
      return new Date(`${ymd}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
    } catch {
      return ymd || '-';
    }
  };

  // ─── Early returns (after all hooks) ────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="page-container">
        <div className="page-content">
          <h2 style={{ fontSize: 18, margin: 0 }}>Accounts</h2>
          <div>Please log in to view accounts dashboard</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content">
          <h2 style={{ fontSize: 18, margin: 0 }}>Accounts</h2>
          <div className="loading-state">Loading...</div>
        </div>
      </div>
    );
  }

  const ViewSwitcher = () => (
    <div style={{ 
      display: 'inline-flex', 
      background: 'rgba(255,255,255,0.06)', 
      borderRadius: 999, 
      padding: 3,
      border: '1px solid rgba(255,255,255,0.1)'
    }}>
      <button
        type="button"
        onClick={() => handleViewModeChange('stats')}
        style={{
          fontSize: 12,
          padding: '5px 14px',
          borderRadius: 999,
          height: 28,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: viewMode === 'stats' ? 'rgba(255,255,255,0.15)' : 'transparent',
          border: 'none',
          color: viewMode === 'stats' ? '#fff' : 'rgba(255,255,255,0.55)',
          cursor: 'pointer',
          fontWeight: viewMode === 'stats' ? 500 : 400,
          transition: 'all 0.2s ease'
        }}
        title="Dashboard View"
      >
        <FiGrid size={14} /> Dashboard
      </button>
      <button
        type="button"
        onClick={() => handleViewModeChange('calendar')}
        style={{
          fontSize: 12,
          padding: '5px 14px',
          borderRadius: 999,
          height: 28,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: viewMode === 'calendar' ? 'rgba(255,255,255,0.15)' : 'transparent',
          border: 'none',
          color: viewMode === 'calendar' ? '#fff' : 'rgba(255,255,255,0.55)',
          cursor: 'pointer',
          fontWeight: viewMode === 'calendar' ? 500 : 400,
          transition: 'all 0.2s ease'
        }}
        title="Calendar View"
      >
        <FiCalendar size={14} /> Calendar
      </button>
    </div>
  );

  const StatsPanel = ({ children }) => (
    <div style={{ marginTop: 8 }}>
      <div
        style={{
          background: 'linear-gradient(180deg, #1a1a1a 0%, #0d1117 100%)',
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
          padding: 12
        }}
      >
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom: 10 }}>
          <ViewSwitcher />
        </div>
        {children}
      </div>
    </div>
  );

  // Calendar View Component
  const CalendarView = () => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    const monthStartStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const monthEndStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

    // Find max revenue for color scaling
    const maxRevenue = useMemo(() => {
      const vals = Object.values(paidByDay || {}).map(v => Number(v || 0));
      return vals.length > 0 ? Math.max(...vals, 1) : 1;
    }, [paidByDay]);
    
    // Calculate monthly totals
    const monthlyTotal = useMemo(() => {
      return Object.values(paidByDay || {}).reduce((sum, val) => sum + (Number(val || 0)), 0);
    }, [paidByDay]);
    
    const daysWithRevenue = useMemo(() => {
      return Object.values(paidByDay || {}).filter(v => Number(v || 0) > 0).length;
    }, [paidByDay]);
    
    // Calculate total outstanding for the month
    const monthlyOutstanding = useMemo(() => {
      return Object.values(pendingByDay).reduce((sum, val) => sum + (val || 0), 0);
    }, [pendingByDay]);

    // Recurring schedules by next due date (YYYY-MM-DD)
    const recurringByDay = useMemo(() => {
      const map = {};
      (recurringRows || []).forEach(r => {
        if (String(r?.status || '').toUpperCase() !== 'ACTIVE') return;
        const next = String(r?.nextInvoiceDate || '');
        if (!next || !/^\d{4}-\d{2}-\d{2}$/.test(next)) return;
        if (!map[next]) map[next] = [];
        map[next].push(r);
      });
      return map;
    }, [recurringRows]);

    const recurringDueInMonth = useMemo(() => {
      let count = 0;
      Object.keys(recurringByDay || {}).forEach(k => {
        if (k >= monthStartStr && k <= monthEndStr) count += (recurringByDay[k] || []).length;
      });
      return count;
    }, [recurringByDay, monthStartStr, monthEndStr]);

    const recurringDueForSelectedDay = useMemo(() => {
      const key = selectedCalendarDay?.dateStr;
      if (!key) return [];
      return recurringByDay[key] || [];
    }, [recurringByDay, selectedCalendarDay?.dateStr]);
    
    // Generate calendar grid
    const calendarDays = [];
    // Empty cells for days before the first day of month
    for (let i = 0; i < startDayOfWeek; i++) {
      calendarDays.push({ day: null, paid: 0, pending: 0 });
    }
    // Days of the month
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const paid = Number(paidByDay?.[dateStr] || 0);
      const pending = pendingByDay[dateStr] || 0;
      const recurringCount = (recurringByDay[dateStr] || []).length;
      calendarDays.push({ day: d, paid, pending, recurringCount, dateStr });
    }
    
    const getHeatColor = (paid) => {
      if (!paid || paid <= 0) return 'rgba(255,255,255,0.03)';
      const intensity = Math.min(1, paid / maxRevenue);
      if (intensity < 0.25) return `rgba(121, 179, 138, 0.25)`;
      if (intensity < 0.5) return `rgba(121, 179, 138, 0.45)`;
      if (intensity < 0.75) return `rgba(121, 179, 138, 0.65)`;
      return `rgba(121, 179, 138, 0.9)`;
    };
    
    const prevMonth = () => {
      setCalendarMonth(prev => {
        if (prev.month === 0) return { year: prev.year - 1, month: 11 };
        return { ...prev, month: prev.month - 1 };
      });
      setSelectedCalendarDay(null);
    };
    
    const nextMonth = () => {
      setCalendarMonth(prev => {
        if (prev.month === 11) return { year: prev.year + 1, month: 0 };
        return { ...prev, month: prev.month + 1 };
      });
      setSelectedCalendarDay(null);
    };

    // Source options for filter
    const sourceOptions = [
      { value: '', label: 'All Sources' },
      { value: 'POS', label: 'POS' },
      { value: 'DIRECT', label: 'Direct' },
      { value: 'CONVERTED_QUOTE', label: 'Converted Quote' },
      { value: 'CUSTOMER_ORDER', label: 'Customer Order' },
      { value: 'QUOTE_ADVANCE', label: 'Quote Advance' },
      { value: 'RECURRING_PROFORMA', label: 'Recurring' }
    ];

    // Status options for filter
    const statusOptions = [
      { value: '', label: 'All Status' },
      { value: 'PAID', label: 'Paid' },
      { value: 'DUE', label: 'Due' },
      { value: 'OVERDUE', label: 'Overdue' },
      { value: 'PARTIAL', label: 'Partial' }
    ];

    const selectStyle = {
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 6,
      padding: '6px 10px',
      fontSize: 12,
      color: '#fff',
      cursor: 'pointer',
      minWidth: 120
    };

    // Calculate selected day totals by status
    const dayTotals = useMemo(() => {
      let paid = 0, pending = 0, overdue = 0, total = 0;
      dayTransactions.forEach(inv => {
        const amt = Number(inv.total || 0);
        total += amt;
        if (inv.status === 'PAID') paid += amt;
        else if (inv.status === 'OVERDUE') overdue += amt;
        else pending += amt; // DUE, DRAFT, etc.
      });
      return { paid, pending, overdue, total, pendingTotal: pending + overdue };
    }, [dayTransactions]);
    const selectedDayTotal = dayTotals.total;
    
    return (
      <div style={{ marginTop: 8 }}>
        <div
          style={{
            background: 'linear-gradient(180deg, #1a1a1a 0%, #0d1117 100%)',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
            padding: 16
          }}
        >
          {/* Header with view switcher */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={prevMonth}
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 6,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 500
                }}
              >
                ‹
              </button>
              <div style={{ minWidth: 140, textAlign: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: '#fff' }}>
                  {monthNames[month]} {year}
                </span>
              </div>
              <button
                onClick={nextMonth}
                style={{
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 6,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#fff',
                  fontSize: 16,
                  fontWeight: 500
                }}
              >
                ›
              </button>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                value={calendarFilters.source}
                onChange={(e) => setCalendarFilters(f => ({ ...f, source: e.target.value }))}
                style={selectStyle}
              >
                {sourceOptions.map(opt => (
                  <option key={opt.value} value={opt.value} style={{ background: '#1a1a1a' }}>{opt.label}</option>
                ))}
              </select>
              <select
                value={calendarFilters.status}
                onChange={(e) => setCalendarFilters(f => ({ ...f, status: e.target.value }))}
                style={selectStyle}
              >
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value} style={{ background: '#1a1a1a' }}>{opt.label}</option>
                ))}
              </select>
              <ViewSwitcher />
            </div>
          </div>

          {/* Main content: Calendar + Side panel */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 10 }}>
            {/* Left: Calendar */}
            <div>
              {/* Summary stats */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(5, 1fr)', 
                gap: 6, 
                marginBottom: 12,
                padding: 10,
                background: 'rgba(255,255,255,0.03)',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.06)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: matte.green }}>{formatInr(monthlyTotal)}</div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>Received</div>
                </div>
                <div style={{ textAlign: 'center', borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: monthlyOutstanding > 0 ? matte.red : 'rgba(255,255,255,0.4)' }}>
                    {formatInr(monthlyOutstanding)}
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>Outstanding</div>
                </div>
                <div style={{ textAlign: 'center', borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{daysWithRevenue}</div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>Active Days</div>
                </div>
                <div style={{ textAlign: 'center', borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: matte.amber }}>
                    {daysWithRevenue > 0 ? formatInr(monthlyTotal / daysWithRevenue) : '-'}
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>Avg/Day</div>
                </div>
                <div style={{ textAlign: 'center', borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: matte.blue }}>{formatInr(maxRevenue)}</div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 1 }}>Peak</div>
                </div>
              </div>
              {/* Recurring mini-summary */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
                marginTop: -6,
                marginBottom: 10,
                padding: '8px 10px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.05)'
              }}>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                  Recurring due this month
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/recurring-invoices')}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 999,
                    padding: '4px 10px',
                    color: '#fff',
                    fontSize: 11,
                    cursor: 'pointer'
                  }}
                  title="Open Recurring Invoices"
                >
                  {recurringDueInMonth} due →
                </button>
              </div>

              {/* Day headers */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)', 
                gap: 1, 
                marginBottom: 2 
              }}>
                {dayNames.map((day, i) => (
                  <div 
                    key={i} 
                    style={{ 
                      textAlign: 'center', 
                      fontSize: 10, 
                      fontWeight: 500, 
                      color: 'rgba(255,255,255,0.4)',
                      padding: '4px 0'
                    }}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid - compact */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)', 
                gap: 2 
              }}>
                {calendarDays.map((cell, idx) => {
                  const isToday = cell.day && 
                    year === new Date().getFullYear() && 
                    month === new Date().getMonth() && 
                    cell.day === new Date().getDate();
                  const isSelected = selectedCalendarDay?.dateStr === cell.dateStr;
                  
                  const handleCellClick = () => {
                    if (cell.day && cell.dateStr) {
                      setSelectedCalendarDay({ dateStr: cell.dateStr, day: cell.day });
                    }
                  };
                  
                  const hasRevenue = cell.paid > 0 || cell.pending > 0;
                  const hasRecurring = Number(cell.recurringCount || 0) > 0;
                  
                  return (
                    <button
                      type="button"
                      key={idx}
                      onClick={cell.day ? handleCellClick : undefined}
                      style={{
                        background: isSelected 
                          ? 'rgba(116, 160, 214, 0.4)' 
                          : (cell.day ? getHeatColor(cell.paid) : 'transparent'),
                        borderRadius: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        aspectRatio: 1,
                        border: isSelected 
                          ? '2px solid rgba(116, 160, 214, 0.8)' 
                          : (isToday ? '2px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.04)'),
                        transition: 'all 0.12s ease',
                        cursor: cell.day ? 'pointer' : 'default',
                        height: '100%',
                        padding: 2
                      }}
                      title={cell.day ? `${cell.dateStr}: Received ${formatInr(cell.paid)}${cell.pending > 0 ? `, Pending ${formatInr(cell.pending)}` : ''}${hasRecurring ? `, Recurring ${cell.recurringCount}` : ''}` : ''}
                    >
                      {cell.day && (
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          overflow: 'hidden',
                          width: '100%',
                          gap: 2
                        }}>
                          <span style={{ 
                            fontSize: 16, 
                            fontWeight: isToday || isSelected ? 600 : 500, 
                            color: hasRevenue || isSelected ? '#fff' : 'rgba(255,255,255,0.4)',
                            lineHeight: 1,
                            textShadow: hasRevenue ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
                          }}>
                            {cell.day}
                          </span>
                          {/* Recurring marker */}
                          {hasRecurring && (
                            <span style={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              minWidth: 16,
                              height: 16,
                              padding: '0 5px',
                              borderRadius: 999,
                              background: 'rgba(138, 123, 184, 0.35)',
                              border: '1px solid rgba(138, 123, 184, 0.65)',
                              color: '#fff',
                              fontSize: 10,
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1
                            }}>
                              {cell.recurringCount}
                            </span>
                          )}
                          {(cell.paid > 0 || cell.pending > 0) && (
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              gap: 1,
                              overflow: 'hidden',
                              width: '100%'
                            }}>
                              {cell.paid > 0 && (
                                <span style={{ 
                                  fontSize: 11, 
                                  color: '#fff', 
                                  fontWeight: 500,
                                  lineHeight: 1.1,
                                  whiteSpace: 'nowrap',
                                  textShadow: '0 1px 3px rgba(0,0,0,0.6)'
                                }}>
                                  {(cell.paid / 1000).toFixed(0)}k
                                </span>
                              )}
                              {cell.pending > 0 && (
                                <span style={{ 
                                  fontSize: 10, 
                                  color: matte.red, 
                                  fontWeight: 500,
                                  lineHeight: 1.1,
                                  whiteSpace: 'nowrap',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                                }}>
                                  {(cell.pending / 1000).toFixed(0)}k
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Heat legend */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 6, 
                marginTop: 10,
                paddingTop: 8,
                borderTop: '1px solid rgba(255,255,255,0.06)'
              }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Less</span>
                <div style={{ display: 'flex', gap: 2 }}>
                  {['rgba(255,255,255,0.03)', 'rgba(121,179,138,0.25)', 'rgba(121,179,138,0.45)', 'rgba(121,179,138,0.65)', 'rgba(121,179,138,0.9)'].map((c, i) => (
                    <div 
                      key={i} 
                      style={{ 
                        width: 14, 
                        height: 14, 
                        borderRadius: 2, 
                        background: c,
                        border: '1px solid rgba(255,255,255,0.08)'
                      }} 
                    />
                  ))}
                </div>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>More</span>
              </div>
            </div>

            {/* Right: Transaction details panel */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.06)',
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 400,
              maxHeight: 'calc(100vh - 100px)',
              overflow: 'hidden'
            }}>
              {selectedCalendarDay ? (
                <>
                  <div style={{ 
                    marginBottom: 12,
                    paddingBottom: 10,
                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>
                        {new Date(`${selectedCalendarDay.dateStr}T00:00:00`).toLocaleDateString('en-IN', { 
                          weekday: 'short', 
                          day: 'numeric', 
                          month: 'short' 
                        })}
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                        {dayTransactions.length} txn{dayTransactions.length !== 1 ? 's' : ''}{recurringDueForSelectedDay.length ? ` • ${recurringDueForSelectedDay.length} recurring` : ''}
                      </div>
                    </div>
                    {/* Amount breakdown */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <div style={{ 
                        background: 'rgba(121,179,138,0.15)', 
                        borderRadius: 6, 
                        padding: '8px 10px',
                        border: '1px solid rgba(121,179,138,0.25)'
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: matte.green }}>{formatInr(dayTotals.paid)}</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 1, textTransform: 'uppercase' }}>Received</div>
                      </div>
                      <div style={{ 
                        background: dayTotals.pendingTotal > 0 ? 'rgba(197,120,120,0.15)' : 'rgba(255,255,255,0.04)', 
                        borderRadius: 6, 
                        padding: '8px 10px',
                        border: dayTotals.pendingTotal > 0 ? '1px solid rgba(197,120,120,0.25)' : '1px solid rgba(255,255,255,0.06)'
                      }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: dayTotals.pendingTotal > 0 ? matte.red : 'rgba(255,255,255,0.4)' }}>
                          {formatInr(dayTotals.pendingTotal)}
                        </div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 1, textTransform: 'uppercase' }}>
                          {dayTotals.overdue > 0 ? 'Overdue' : 'Pending'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recurring due list */}
                  {recurringDueForSelectedDay.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>Recurring due</div>
                        <button
                          type="button"
                          onClick={() => navigate('/recurring-invoices')}
                          style={{
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: 999,
                            padding: '4px 10px',
                            color: '#fff',
                            fontSize: 11,
                            cursor: 'pointer'
                          }}
                        >
                          View recurring →
                        </button>
                      </div>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {recurringDueForSelectedDay.slice(0, 5).map((r) => {
                          const customerName = r?.customer?.name || r?.customerSnapshot?.name || 'Customer';
                          const interval = r?.billingInterval || '-';
                          const mult = Number(r?.intervalCount || 1);
                          const intervalLabel = mult > 1 ? `${interval} ×${mult}` : interval;
                          return (
                            <div
                              key={r.id || `${customerName}-${selectedCalendarDay.dateStr}`}
                              style={{
                                background: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderRadius: 8,
                                padding: '8px 10px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 10
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: '#fff', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {customerName}
                                </div>
                                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {r?.name || 'Recurring invoice'} • {intervalLabel}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => navigate(`/recurring-invoice/${r.id}/edit`)}
                                style={{
                                  background: 'rgba(255,255,255,0.06)',
                                  border: '1px solid rgba(255,255,255,0.12)',
                                  borderRadius: 8,
                                  padding: '6px 10px',
                                  color: '#fff',
                                  fontSize: 11,
                                  cursor: 'pointer',
                                  flexShrink: 0
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          );
                        })}
                        {recurringDueForSelectedDay.length > 5 && (
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                            +{recurringDueForSelectedDay.length - 5} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {loadingTransactions ? (
                    <div style={{ 
                      flex: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: 12
                    }}>
                      {calendarInvoicesLoading ? 'Loading…' : 'Loading…'}
                    </div>
                  ) : dayTransactions.length > 0 ? (
                    <div style={{ 
                      flex: 1, 
                      overflowY: 'auto', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: 8 
                    }}>
                      {dayTransactions.map((inv, i) => (
                        <div
                          key={inv.id || i}
                          style={{
                            background: inv.status === 'OVERDUE' ? 'rgba(197,120,120,0.08)' : 'rgba(255,255,255,0.04)',
                            border: inv.status === 'OVERDUE' ? '1px solid rgba(197,120,120,0.2)' : '1px solid rgba(255,255,255,0.08)',
                            borderRadius: 8,
                            padding: '8px 10px',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {/* Row 1: Invoice info + Amount */}
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            {/* Status dot */}
                            <div style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: inv.status === 'PAID' ? matte.green : 
                                         inv.status === 'OVERDUE' ? matte.red : 
                                         matte.amber,
                              flexShrink: 0,
                              marginTop: 4
                            }} />
                            {/* Invoice & Customer & Source */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ 
                                  fontSize: 11, 
                                  fontWeight: 300, 
                                  color: '#fff',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {inv.number || `INV-${String(inv.id || '').slice(-6)}`}
                                </span>
                                <span style={{ 
                                  fontSize: 8, 
                                  color: sourceColors[inv.createdFrom] || 'rgba(255,255,255,0.4)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                  flexShrink: 0
                                }}>
                                  {sourceLabels[inv.createdFrom] || inv.createdFrom || ''}
                                </span>
                              </div>
                              <div style={{ 
                                fontSize: 10, 
                                color: 'rgba(255,255,255,0.5)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {inv.customerSnapshot?.name || 'Customer'}
                              </div>
                            </div>
                            {/* Amount */}
                            <div style={{ 
                              fontSize: 12, 
                              fontWeight: 600, 
                              color: inv.status === 'PAID' ? matte.green : 
                                     inv.status === 'OVERDUE' ? matte.red : 
                                     matte.amber,
                              whiteSpace: 'nowrap',
                              textAlign: 'right'
                            }}>
                              {formatInr(inv.total || 0)}
                            </div>
                          </div>
                          
                          {/* Row 2: Payment mode (clickable) + View links */}
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginTop: 6,
                            paddingTop: 6,
                            borderTop: '1px solid rgba(255,255,255,0.05)'
                          }}>
                            {/* Payment mode */}
                            <button
                              type="button"
                              onClick={() => openPaymentDetails(inv.id)}
                              style={{
                                background: 'rgba(255,255,255,0.06)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 4,
                                padding: '2px 6px',
                                fontSize: 9,
                                color: 'rgba(255,255,255,0.7)',
                                cursor: 'pointer',
                                textTransform: 'uppercase'
                              }}
                            >
                              {inv.paymentMethod ? String(inv.paymentMethod) : 'Payment'}
                            </button>
                            
                            {/* View links */}
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={() => openInvoicePdf(inv.id, inv.number)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: '2px 4px',
                                  fontSize: 10,
                                  color: matte.blue,
                                  cursor: 'pointer',
                                 // textDecoration: 'underline'
                                }}
                              >
                                Invoice
                              </button>
                              {/* Show Quote link if has sourceQuotes */}
                              {inv.sourceQuotes?.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => openQuotePdf(inv.sourceQuotes[0].id, inv.sourceQuotes[0].number)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '2px 4px',
                                    fontSize: 10,
                                    color: matte.teal,
                                    cursor: 'pointer',
                                   // textDecoration: 'underline'
                                  }}
                                >
                                  Quote
                                </button>
                              )}
                              {/* Show Proforma link if createdFrom is RECURRING_PROFORMA */}
                              {inv.createdFrom === 'RECURRING_PROFORMA' && inv.recurringProformaId && (
                                <button
                                  type="button"
                                  onClick={() => navigate(`/proformas/${inv.recurringProformaId}`)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '2px 4px',
                                    fontSize: 10,
                                    color: matte.purple,
                                    cursor: 'pointer',
                                   // textDecoration: 'underline'
                                  }}
                                >
                                  Proforma
                                </button>
                              )}
                              {/* Show Order link if createdFrom is CUSTOMER_ORDER */}
                              {inv.createdFrom === 'CUSTOMER_ORDER' && inv.orderId && (
                                <button
                                  type="button"
                                  onClick={() => navigate(`/orders/${inv.orderId}`)}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '2px 4px',
                                    fontSize: 10,
                                    color: matte.amber,
                                    cursor: 'pointer',
                                   // textDecoration: 'underline'
                                  }}
                                >
                                  Order
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      flex: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: 12
                    }}>
                      No transactions
                    </div>
                  )}

                  {/* View all link */}
                  {dayTransactions.length > 0 && (
                    <button
                      type="button"
                      onClick={() => navigate(`/invoices?fromDate=${selectedCalendarDay.dateStr}&toDate=${selectedCalendarDay.dateStr}&dateType=EFFECTIVE`)}
                      style={{
                        marginTop: 10,
                        padding: '8px 12px',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 6,
                        color: 'rgba(255,255,255,0.75)',
                        fontSize: 11,
                        cursor: 'pointer',
                        textAlign: 'center'
                      }}
                    >
                      View all in Invoices →
                    </button>
                  )}
                </>
              ) : (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.35)',
                  gap: 8
                }}>
                  <FiCalendar size={32} style={{ opacity: 0.5 }} />
                  <span style={{ fontSize: 12 }}>Select a day to view transactions</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment details modal */}
          {paymentDetailsModal.open && (
            <div
              role="dialog"
              aria-modal="true"
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: 16
              }}
              onClick={closePaymentDetails}
            >
              <div
                style={{
                  width: 'min(640px, 96vw)',
                  background: 'linear-gradient(180deg, #1a1a1a 0%, #0d1117 100%)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 12,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
                  padding: 14
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>Payment details</div>
                  <button
                    type="button"
                    onClick={closePaymentDetails}
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#fff',
                      borderRadius: 8,
                      height: 28,
                      padding: '0 10px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>

                {paymentDetailsModal.loading ? (
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, padding: 10 }}>Loading…</div>
                ) : paymentDetailsModal.error ? (
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, padding: 10 }}>{paymentDetailsModal.error}</div>
                ) : (
                  (() => {
                    const inv = paymentDetailsModal.invoice;
                    if (!inv) return <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, padding: 10 }}>No data</div>;
                    const balance = Math.max(0, Number(inv.total || 0) - Number(inv.amountPaid || 0));
                    const fmtDate = (d) => {
                      try { return d ? new Date(d).toLocaleString('en-IN') : '-'; } catch { return '-'; }
                    };
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 12 }}>
                          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, textTransform: 'uppercase' }}>Invoice</div>
                          <div style={{ color: '#fff', fontSize: 13, fontWeight: 600, marginTop: 4 }}>{inv.number || `INV-${inv.id}`}</div>
                          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, marginTop: 4 }}>{inv.customerSnapshot?.name || 'Customer'}</div>
                          <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => openInvoicePdf(inv.id, inv.number)}
                              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '6px 10px', color: '#fff', fontSize: 12, cursor: 'pointer' }}
                            >
                              Open Invoice PDF
                            </button>
                          </div>
                        </div>

                        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 12 }}>
                          <div style={{ display: 'grid', gap: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
                              <span>Total</span><span style={{ color: '#fff', fontWeight: 600 }}>{formatInr(inv.total || 0)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
                              <span>Paid</span><span style={{ color: matte.green, fontWeight: 600 }}>{formatInr(inv.amountPaid || 0)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
                              <span>Balance</span><span style={{ color: balance > 0 ? matte.red : matte.green, fontWeight: 600 }}>{formatInr(balance)}</span>
                            </div>
                            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
                              <span>Payment method</span><span style={{ color: '#fff', fontWeight: 500 }}>{inv.paymentMethod || '-'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
                              <span>Reference</span><span style={{ color: '#fff', fontWeight: 500 }}>{inv.paymentReference || '-'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'rgba(255,255,255,0.75)' }}>
                              <span>Payment date</span><span style={{ color: '#fff', fontWeight: 500 }}>{fmtDate(inv.paymentDate)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          )}

          {/* PDF Preview Side Modal */}
          {pdfModal.open && (
            <div
              role="dialog"
              aria-modal="true"
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                display: 'flex',
                justifyContent: 'flex-end',
                zIndex: 9999
              }}
              onClick={closePdfModal}
            >
              <div
                style={{
                  width: 'min(800px, 90vw)',
                  height: '100vh',
                  background: 'linear-gradient(180deg, #1a1a1a 0%, #0d1117 100%)',
                  borderLeft: '1px solid rgba(255,255,255,0.10)',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '-20px 0 60px rgba(0,0,0,0.45)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  padding: '12px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)'
                }}>
                  <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{pdfModal.label || 'Document'}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {pdfModal.url && (
                      <button
                        type="button"
                        onClick={() => window.open(pdfModal.url, '_blank', 'noopener,noreferrer')}
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          color: '#fff',
                          borderRadius: 6,
                          height: 28,
                          padding: '0 10px',
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        Open in New Tab
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={closePdfModal}
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        color: '#fff',
                        borderRadius: 6,
                        height: 28,
                        padding: '0 12px',
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  {pdfModal.loading ? (
                    <div style={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.6)',
                      fontSize: 13
                    }}>
                      Loading PDF...
                    </div>
                  ) : pdfModal.url ? (
                    <iframe
                      src={pdfModal.url}
                      title={pdfModal.label || 'PDF Preview'}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        background: '#fff'
                      }}
                    />
                  ) : (
                    <div style={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: 13
                    }}>
                      No PDF available
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const Card = ({ title, subtitle, children }) => (
    <div style={{
      borderRadius: 12,
      padding: 12,
      background: 'linear-gradient(180deg, #1a1a1a 0%, #0d1117 100%)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: '0 10px 25px rgba(0,0,0,0.12)'
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', gap:12 }}>
        <div style={{ fontWeight: 400, color:'#fff', fontSize: 13 }}>{title}</div>
        <div style={{ color:'rgba(255,255,255,0.78)', fontSize: 11 }}>{subtitle}</div>
      </div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );

  const SegBar = ({ segments }) => {
    const totalSeg = segments.reduce((a, s) => a + Number(s.value || 0), 0) || 1;
    return (
      <div style={{ display:'flex', height: 10, borderRadius: 5, overflow:'hidden', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.08)' }}>
        {segments.map((s, i) => (
          <button
            key={i}
            type="button"
            onClick={s.onClick}
            title={s.title}
            style={{
              width: `${(Number(s.value || 0) / totalSeg) * 100}%`,
              background: s.color,
              border: 'none',
              padding: 0,
              cursor: s.onClick ? 'pointer' : 'default',
              opacity: 0.9,
              borderRadius: 0,
              borderTopLeftRadius: i === 0 ? 5 : 0,
              borderBottomLeftRadius: i === 0 ? 5 : 0,
              borderTopRightRadius: i === segments.length - 1 ? 5 : 0,
              borderBottomRightRadius: i === segments.length - 1 ? 5 : 0
            }}
          />
        ))}
      </div>
    );
  };

  const Legend = ({ items }) => (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginTop: 10 }}>
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          onClick={it.onClick}
          style={{
            display:'flex',
            justifyContent:'space-between',
            alignItems:'center',
            gap:10,
            width:'100%',
            background:'rgba(255,255,255,0.06)',
            border: it.active ? '1px solid rgba(255,255,255,0.65)' : '1px solid rgba(255,255,255,0.10)',
            color:'#fff',
            padding:'8px 10px',
            borderRadius: 12,
            cursor: it.onClick ? 'pointer' : 'default'
          }}
          title={it.title}
        >
          <span style={{ display:'inline-flex', alignItems:'center', gap:8, color:'rgba(255,255,255,0.9)', fontSize: 12 }}>
            <span style={{ width:10, height:10, borderRadius: 3, background: it.color }} />
            {it.label}
          </span>
          <span style={{ fontWeight: 400 }}>{it.value}</span>
        </button>
      ))}
    </div>
  );

  // ─── Computed Stats ───────────────────────────────────────────────────────
  const amountReceived = Number(bizStats?.amountReceived || 0);
  const amountDue = Number(bizStats?.amountDue || 0);
  const totalBilling = Math.max(0, amountReceived + amountDue);
  const collectionPct = totalBilling ? Math.round((amountReceived / totalBilling) * 100) : 0;

  const quotesCreatedCount = Number(bizStats?.quotesCreatedCount || 0);
  const ordersReceivedCount = Number(bizStats?.ordersReceivedCount || 0);
  const invoicesPaidCount = Number(bizStats?.invoicesPaidCount || 0);
  const quoteToOrderPct = quotesCreatedCount ? Math.round((ordersReceivedCount / quotesCreatedCount) * 100) : null;
  const orderToPaidPct = ordersReceivedCount ? Math.round((invoicesPaidCount / ordersReceivedCount) * 100) : null;

  // Avg invoice value (ticket size)
  const avgInvoiceValue = invoicesPaidCount ? (amountReceived / invoicesPaidCount) : null;

  // Run-rate & projection based on selected date range
  const rangeFrom = selectedDateRange?.startDate ? new Date(selectedDateRange.startDate) : null;
  const rangeTo = selectedDateRange?.endDate ? new Date(selectedDateRange.endDate) : null;
  const daysInRange = (rangeFrom && rangeTo) ? Math.max(1, Math.ceil((rangeTo - rangeFrom) / (1000*60*60*24)) + 1) : null;
  const dailyRunRate = (daysInRange && amountReceived) ? (amountReceived / daysInRange) : null;
  const projected30 = (dailyRunRate != null) ? (dailyRunRate * 30) : null;

  // POS stats
  const posReceived = Number(bizStats?.posAmountReceived || 0);
  const posSalesCount = Number(bizStats?.posSalesCount || 0);
  const posSharePct = amountReceived ? Math.round((posReceived / amountReceived) * 100) : null;
  const avgPosValue = posSalesCount ? (posReceived / posSalesCount) : null;
  const nonPosReceived = Math.max(0, amountReceived - posReceived);
  const nonPosCount = Math.max(0, invoicesPaidCount - posSalesCount);

  // Derived values from allSources
  const sourceCount = allSources.length;
  const topSourcePct = (allSources[0]?.amount && sourceBreakdown.total) ? Math.round((allSources[0].amount / sourceBreakdown.total) * 100) : null;

  // Sales pipeline funnel sizing
  const funnelMax = Math.max(1, quotesCreatedCount, ordersReceivedCount, invoicesPaidCount);
  const funnelWidthPct = (v) => Math.max(18, Math.min(100, (Number(v || 0) / funnelMax) * 100));

  return (
    <div className="page-container" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #161b22 100%)', minHeight: '100vh' }}>
      <style>{`
        .dark-date-filters .header-date-filters .drp-input {
          background: rgba(255,255,255,0.08) !important;
          border: 1px solid rgba(255,255,255,0.15) !important;
          color: #fff !important;
        }
        .dark-date-filters .header-date-filters .drp-input:hover {
          background: rgba(255,255,255,0.12) !important;
          border-color: rgba(255,255,255,0.25) !important;
        }
        .dark-date-filters .header-date-filters .date-separator {
          color: rgba(255,255,255,0.5) !important;
        }
        .dark-date-filters .header-date-filters .drp-clear {
          background: rgba(255,255,255,0.08) !important;
          border: 1px solid rgba(255,255,255,0.15) !important;
          color: #fff !important;
        }
        .dark-date-filters .header-date-filters .drp-clear:hover {
          background: rgba(255,255,255,0.15) !important;
        }
      `}</style>
      <div className="page-content" style={{ background: 'transparent' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 4 }}>
          <h2 style={{ fontSize: 18, margin: 0, color: '#fff' }}>Accounts Overview</h2>
          {viewMode === 'stats' && (
            <div style={{ display:'flex', gap:8, alignItems:'center' }} className="dark-date-filters">
              <HeaderDateFilters value={selectedDateRange} onChange={setSelectedDateRange} />
            </div>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* View Toggle: Stats Dashboard or Calendar */}
        {viewMode === 'stats' ? (
          <StatsPanel>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:12 }}>

              {/* ─── Revenue Card ─────────────────────────────────────────── */}
              <Card title="Revenue" subtitle="Collected vs Outstanding">
                <SegBar segments={[
                  { value: amountReceived, color: matte.green, title: `Collected: ${formatInr(amountReceived)}`, onClick: () => navigate(`/invoices?status=PAID&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`) },
                  { value: amountDue, color: matte.red, title: `Outstanding: ${formatInr(amountDue)}`, onClick: () => navigate(`/invoices?status=DUE,OVERDUE&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`) }
                ]} />
                <Legend items={[
                  { label: 'Collected', value: formatInr(amountReceived), color: matte.green, onClick: () => navigate(`/invoices?status=PAID&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`), active: false, title: 'Total received from paid invoices' },
                  { label: 'Outstanding', value: formatInr(amountDue), color: matte.red, onClick: () => navigate(`/invoices?status=DUE,OVERDUE&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`), active: false, title: 'Amount still due on invoices in this period' },
                  { label: 'Collection %', value: `${collectionPct}%`, color: collectionPct >= 80 ? matte.green : (collectionPct >= 50 ? matte.amber : matte.red), onClick: null, active: false, title: 'Collected ÷ Total billed' },
                  { label: 'Avg ticket', value: avgInvoiceValue != null ? formatInr(avgInvoiceValue) : '-', color: matte.purple, onClick: null, active: false, title: 'Average value per paid invoice' }
                ]} />
                <div style={{ marginTop: 12, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize: 12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.85)' }}>
                    <span>Daily run-rate</span>
                    <span style={{ color:'#fff', fontWeight: 400 }}>{dailyRunRate != null ? formatInr(dailyRunRate) : '-'}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.85)' }}>
                    <span>30-day projection</span>
                    <span style={{ color:'#fff', fontWeight: 400 }}>{projected30 != null ? formatInr(projected30) : '-'}</span>
                  </div>
                </div>

                {/* Sales pipeline (horizontal funnel) */}
                <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color:'rgba(255,255,255,0.75)' }}>Sales pipeline</div>
                    <div style={{ fontSize: 11, color:'rgba(255,255,255,0.45)' }}>This period</div>
                  </div>

                  <SegBar segments={[
                    { value: quotesCreatedCount, color: matte.teal, title: `Quotes created: ${quotesCreatedCount}`, onClick: () => navigate(`/quotes?fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`) },
                    { value: ordersReceivedCount, color: matte.amber, title: `Orders received: ${ordersReceivedCount}`, onClick: () => navigate(`/orders?fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`) },
                    { value: invoicesPaidCount, color: matte.green, title: `Invoices paid: ${invoicesPaidCount}`, onClick: () => navigate(`/invoices?status=PAID&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`) }
                  ]} />

                  <Legend items={[
                    { label: 'Quotes created', value: quotesCreatedCount, color: matte.teal, onClick: () => navigate(`/quotes?fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`), active: false, title: 'Quotes created in period' },
                    { label: 'Orders received', value: ordersReceivedCount, color: matte.amber, onClick: () => navigate(`/orders?fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`), active: false, title: 'Orders received in period' },
                    { label: 'Invoices paid', value: invoicesPaidCount, color: matte.green, onClick: () => navigate(`/invoices?status=PAID&fromDate=${toDateStr(selectedDateRange?.startDate)}&toDate=${toDateStr(selectedDateRange?.endDate)}`), active: false, title: 'Invoices paid in period' },
                    { label: 'Recurring active', value: recurringActiveCount, color: matte.indigo, onClick: () => navigate('/recurring-invoices'), active: false, title: 'Active recurring billing schedules' }
                  ]} />

                  <div style={{ marginTop: 10, display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, fontSize: 12 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.85)' }}>
                      <span>Avg quote</span>
                      <span style={{ color:'#fff', fontWeight: 400 }}>{quotesCreatedCount && amountReceived ? formatInr(amountReceived / quotesCreatedCount) : '-'}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.85)' }}>
                      <span>Avg POS</span>
                      <span style={{ color: matte.blue, fontWeight: 400 }}>{avgPosValue != null ? formatInr(avgPosValue) : '-'}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* ─── Channel Mix Card ─────────────────────────────────────── */}
              <Card title="Revenue by Source" subtitle="Channel mix + breakdown">
                <div style={{ display:'grid', gridTemplateColumns:'110px 1fr', gap:12, alignItems:'center' }}>
                  <DonutChart
                    data={allSources.map(s => ({ label: s.label, value: s.amount || 0, color: s.color }))}
                  />
                  <div style={{ display:'grid', gap:6, fontSize: 12 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.92)' }}>
                      <span>POS</span>
                      <span style={{ fontWeight: 400, color: matte.blue }}>{formatInr(posReceived)} <span style={{ color:'rgba(255,255,255,0.5)' }}>({posSharePct ?? 0}%)</span></span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.92)' }}>
                      <span>Other</span>
                      <span style={{ fontWeight: 400, color: matte.teal }}>{formatInr(nonPosReceived)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.65)' }}>
                      <span>Channels</span>
                      <span style={{ fontWeight: 400 }}>{sourceCount}</span>
                    </div>
                    {allSources[0] && (
                      <div style={{ display:'flex', justifyContent:'space-between', color:'rgba(255,255,255,0.65)' }}>
                        <span>Top</span>
                        <span style={{ fontWeight: 400 }}>{allSources[0].label} ({topSourcePct}%)</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Full breakdown (merged from the old "Revenue by Source" card) */}
                <div style={{ marginTop: 12, borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color:'rgba(255,255,255,0.7)' }}>All channels</div>
                    <div style={{ fontSize: 11, color:'rgba(255,255,255,0.45)' }}>{formatInr(sourceBreakdown.total || 0)}</div>
                  </div>
                  <div style={{ display:'grid', gap:8, maxHeight: 210, overflowY:'auto' }}>
                    {allSources.map((s, i) => {
                      const pct = Math.round(((s.amount || 0) / (Number(sourceBreakdown.total || 0) || 1)) * 100);
                      return (
                        <div key={`${s.source}-${i}`} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, fontSize: 12 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:10, minWidth: 0, flex:1 }}>
                            <span style={{ width: 10, height: 10, background: s.color, display: 'inline-block', borderRadius: 2, flexShrink:0 }} />
                            <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:'rgba(255,255,255,0.92)' }}>{s.label}</span>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:10, minWidth: 150, justifyContent:'flex-end' }}>
                            <span style={{ color:'rgba(255,255,255,0.55)', minWidth: 36, textAlign:'right' }}>{pct}%</span>
                            <span style={{ fontWeight: 500, textAlign:'right', color:'#fff' }}>{formatInr(s.amount || 0)}</span>
                            <span style={{ color:'rgba(255,255,255,0.35)', minWidth: 24, textAlign:'right', fontSize:11 }}>×{s.count}</span>
                          </div>
                        </div>
                      );
                    })}
                    {allSources.length === 0 && (
                      <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12 }}>No revenue data</div>
                    )}
                  </div>
                </div>
              </Card>

              {/* ─── Upcoming Recurring Invoices ─────────────────────────── */}
              <Card
                title="Recurring billing"
                subtitle={
                  recurringLoading
                    ? 'Loading…'
                    : `${recurringActiveCount} active • ${recurringDueMeta.dueToday} due today • ${recurringDueMeta.overdue} overdue`
                }
              >
                <div style={{ display: 'grid', gap: 5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>Upcoming</div>
                    <button
                      type="button"
                      onClick={() => navigate('/recurring-invoices')}
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 8,
                        padding: '6px 10px',
                        color: '#fff',
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      View all →
                    </button>
                  </div>

                  {recurringUpcoming.length === 0 ? (
                    <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, padding: '6px 0' }}>
                      No upcoming recurring invoices
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>
                      {recurringUpcoming.map((r) => {
                        const customerName = r?.customer?.name || r?.customerSnapshot?.name || 'Customer';
                        const interval = r?.billingInterval || '-';
                        const mult = Number(r?.intervalCount || 1);
                        const intervalLabel = mult > 1 ? `${interval} ×${mult}` : interval;
                        const next = String(r?.nextInvoiceDate || '');
                        return (
                          <div
                            key={r.id || `${customerName}-${next}`}
                            style={{
                              background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              borderRadius: 10,
                              padding: '8px 10px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 10
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ color: '#fff', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {customerName}
                              </div>
                              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {r?.name || 'Recurring invoice'} • {intervalLabel}
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ color: matte.green, fontSize: 12, fontWeight: 600 }}>{fmtYmd(next)}</div>
                                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10 }}>Next due</div>
                              </div>
                              <button
                                type="button"
                                onClick={() => navigate(`/recurring-invoice/${r.id}/edit`)}
                                style={{
                                  background: 'rgba(255,255,255,0.06)',
                                  border: '1px solid rgba(255,255,255,0.12)',
                                  borderRadius: 8,
                                  padding: '5px 10px',
                                  color: '#fff',
                                  fontSize: 12,
                                  cursor: 'pointer'
                                }}
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* ─── Row 2: Revenue Sources Breakdown + Revenue Trend Graph ─── */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop: 12, alignItems:'stretch' }}>
              <Card title="Revenue Trend" subtitle={peakRevenueDay ? `Peak: ${peakRevenueDay.date}` : 'Daily'}>
                <div style={{ display:'flex', flexDirection:'column', minHeight: 200 }}>
                  <div style={{ 
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', 
                    borderRadius: 10, 
                    padding: '12px 10px 12px 10px',
                    border: '1px solid rgba(255,255,255,0.06)'
                  }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 6 }}>
                      <div style={{ fontSize: 10, color:'rgba(255,255,255,0.6)' }}>Revenue by source (daily)</div>
                      <div style={{ fontSize: 10, color:'rgba(255,255,255,0.45)' }}>Top {revenueBySourceSeries.length || 0}</div>
                    </div>
                    <MultiLineChart series={revenueBySourceSeries} yCurrency dark height={150} />
                    <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginTop: 6 }}>
                      {revenueBySourceSeries.map((s) => (
                        <div key={s.name} style={{ display:'flex', alignItems:'center', gap:6, fontSize:10, color:'rgba(255,255,255,0.75)' }}>
                          <span style={{ width:10, height:3, borderRadius:2, background:s.color }} />
                          <span>{s.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer pinned to bottom for alignment with Activity card */}
                  <div style={{ 
                    marginTop: 'auto',
                    paddingTop: 12
                  }}>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr 1fr', 
                      gap: 6,
                      background: 'rgba(255,255,255,0.04)',
                      borderRadius: 8,
                      padding: '10px 8px'
                    }}>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                        <span style={{ fontWeight: 500, color:'#fff', fontSize: 13 }}>{formatInr(totalRevenueFromTrend)}</span>
                        <span style={{ color:'rgba(255,255,255,0.45)', fontSize: 10, textTransform:'uppercase', letterSpacing:'0.5px' }}>Total</span>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3, borderLeft:'1px solid rgba(255,255,255,0.08)', borderRight:'1px solid rgba(255,255,255,0.08)' }}>
                        <span style={{ fontWeight: 500, color:'#fff', fontSize: 13 }}>{revenuePerDay.length}</span>
                        <span style={{ color:'rgba(255,255,255,0.45)', fontSize: 10, textTransform:'uppercase', letterSpacing:'0.5px' }}>Days</span>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                        <span style={{ fontWeight: 500, color:'#fff', fontSize: 13 }}>{revenuePerDay.length ? formatInr(totalRevenueFromTrend / revenuePerDay.length) : '-'}</span>
                        <span style={{ color:'rgba(255,255,255,0.45)', fontSize: 10, textTransform:'uppercase', letterSpacing:'0.5px' }}>Avg/day</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card
                title="Activity"
                subtitle={`${quotesPerDay.reduce((s, d) => s + (d.count || 0), 0)} quotes • ${ordersPerDay.reduce((s, d) => s + (d.count || 0), 0)} orders`}
              >
                <div style={{ 
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', 
                  borderRadius: 10, 
                  padding: '12px 10px 10px 10px',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}>
                  <MultiLineChart
                    dark
                    height={220}
                    series={[
                      { name: 'Quotes', color: matte.teal, data: (quotesPerDay || []).map(d => ({ date: d.date, amount: d.count })) },
                      { name: 'Orders', color: matte.amber, data: (ordersPerDay || []).map(d => ({ date: d.date, amount: d.count })) }
                    ]}
                  />
                  <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginTop: 8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:10, color:'rgba(255,255,255,0.75)' }}>
                      <span style={{ width:12, height:3, borderRadius:2, background: matte.teal }} />
                      <span>Quotes</span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:10, color:'rgba(255,255,255,0.75)' }}>
                      <span style={{ width:12, height:3, borderRadius:2, background: matte.amber }} />
                      <span>Orders</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </StatsPanel>
        ) : (
          <CalendarView />
        )}

      </div>
    </div>
  );
};

export default AccountsDashboard;


